/**
 * React hook для управления тренировками
 */

import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { 
  Workout, 
  WorkoutCreate, 
  WorkoutUpdate, 
  WorkoutFilters, 
  ExerciseLog, 
  ExerciseLogCreate, 
  ExerciseLogUpdate,
  PaginatedResponse,
  PaginationMeta 
} from '../types';

export const useWorkouts = (filters: WorkoutFilters = {}) => {
  const queryClient = useQueryClient();

  // Запрос списка тренировок
  const {
    data: workouts = [],
    isLoading,
    error,
    refetch
  } = useQuery(
    ['workouts', filters],
    () => apiService.getWorkouts(filters),
    {
      onError: (error: any) => {
        // Не показываем ошибку 401, так как она обрабатывается interceptor'ом
        if (error?.response?.status === 401 || error?.status === 401) {
          return; // Interceptor обработает эту ошибку
        }
        const errorMessage = error.detail || 'Ошибка при загрузке тренировок';
        toast.error(errorMessage);
      }
    }
  );

  // Запрос общего количества тренировок (без пагинации)
  const countFilters = { ...filters };
  delete countFilters.skip;
  delete countFilters.limit;
  
  const {
    data: countData = { total: 0 },
    isLoading: isCountLoading,
    error: countError
  } = useQuery(
    ['workouts-count', countFilters],
    () => apiService.getWorkoutsCount(countFilters),
    {
      onError: (error: any) => {
        // Не показываем ошибку 401, так как она обрабатывается interceptor'ом
        if (error?.response?.status === 401 || error?.status === 401) {
          return; // Interceptor обработает эту ошибку
        }
        const errorMessage = error.detail || 'Ошибка при загрузке количества тренировок';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для создания тренировки
  const createWorkoutMutation = useMutation(
    (data: WorkoutCreate) => apiService.createWorkout(data),
    {
      onSuccess: () => {
        toast.success('Тренировка создана успешно!');
        queryClient.invalidateQueries('workouts');
        queryClient.invalidateQueries('workouts-count');
        // Инвалидируем кэш для всех клиентов, чтобы обновить статус выполнения
        queryClient.invalidateQueries('clients');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при создании тренировки';
        toast.error(errorMessage);
      }
    }
  );

  // Функция для создания тренировки с кастомным обработчиком успеха
  const createWorkoutWithCallback = (data: WorkoutCreate, onSuccess?: (workout: Workout) => void) => {
    createWorkoutMutation.mutate(data, {
      onSuccess: (workout) => {
        queryClient.invalidateQueries('workouts');
        queryClient.invalidateQueries('workouts-count');
        // Инвалидируем кэш для всех клиентов, чтобы обновить статус выполнения
        queryClient.invalidateQueries('clients');
        if (onSuccess) {
          onSuccess(workout);
        }
      }
    });
  };

  // Мутация для обновления тренировки
  const updateWorkoutMutation = useMutation(
    ({ id, data }: { id: number; data: WorkoutUpdate }) => 
      apiService.updateWorkout(id, data),
    {
      onSuccess: () => {
        toast.success('Тренировка обновлена успешно!');
        queryClient.invalidateQueries('workouts');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при обновлении тренировки';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для удаления тренировки
  const deleteWorkoutMutation = useMutation(
    (id: number) => apiService.deleteWorkout(id),
    {
      onSuccess: () => {
        toast.success('Тренировка удалена успешно!');
        queryClient.invalidateQueries('workouts');
        queryClient.invalidateQueries('workouts-count');
        // Инвалидируем кэш для всех клиентов, чтобы обновить статус выполнения
        queryClient.invalidateQueries('clients');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при удалении тренировки';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для завершения тренировки
  const completeWorkoutMutation = useMutation(
    (id: number) => apiService.updateWorkout(id, { is_completed: true }),
    {
      onSuccess: () => {
        toast.success('Тренировка завершена!');
        queryClient.invalidateQueries('workouts');
        queryClient.invalidateQueries('workouts-count');
        // Инвалидируем кэш для всех клиентов, чтобы обновить статус выполнения
        queryClient.invalidateQueries('clients');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при завершении тренировки';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для удаления клиента из тренировки
  const removeClientFromWorkoutMutation = useMutation(
    ({ workoutId, clientId }: { workoutId: number; clientId: number }) => 
      apiService.removeClientFromWorkout(workoutId, clientId),
    {
      onSuccess: (result) => {
        toast.success(`Клиент удален из тренировки. Удалено ${result.deleted_logs_count} логов и ${result.deleted_exercises_count} упражнений.`);
        queryClient.invalidateQueries('workouts');
        queryClient.invalidateQueries('workouts-count');
        // Инвалидируем кэш для всех клиентов, чтобы обновить статус выполнения
        queryClient.invalidateQueries('clients');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при удалении клиента из тренировки';
        toast.error(errorMessage);
      }
    }
  );

  return {
    // Данные
    workouts,
    totalCount: countData.total,
    isLoading: isLoading || isCountLoading,
    error: error || countError,
    
    // Действия
    createWorkout: createWorkoutMutation.mutate,
    createWorkoutWithCallback,
    updateWorkout: updateWorkoutMutation.mutate,
    deleteWorkout: deleteWorkoutMutation.mutate,
    completeWorkout: completeWorkoutMutation.mutate,
    removeClientFromWorkout: removeClientFromWorkoutMutation.mutate,
    refetch,
    
    // Состояния мутаций
    isCreateLoading: createWorkoutMutation.isLoading,
    isUpdateLoading: updateWorkoutMutation.isLoading,
    isDeleteLoading: deleteWorkoutMutation.isLoading,
    isCompleteLoading: completeWorkoutMutation.isLoading,
    isRemoveClientLoading: removeClientFromWorkoutMutation.isLoading,
  };
};

// Хук для работы с одной тренировкой
export const useWorkout = (id: number) => {
  const queryClient = useQueryClient();

  const {
    data: workout,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['workout', id],
    () => apiService.getWorkout(id),
    {
      enabled: !!id,
      onSuccess: (data) => {
        // Проверяем, есть ли удаленные упражнения
        const deletedExercises = data?.workout_exercises?.filter(we => !we.exercise) || [];
        if (deletedExercises.length > 0) {
          console.warn(`Workout ${id} contains ${deletedExercises.length} deleted exercises`);
        }
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при загрузке тренировки';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для обновления тренировки
  const updateWorkoutMutation = useMutation(
    (data: WorkoutUpdate) => apiService.updateWorkout(id, data),
    {
      onSuccess: () => {
        toast.success('Тренировка обновлена успешно!');
        queryClient.invalidateQueries(['workout', id]);
        queryClient.invalidateQueries('workouts');
        queryClient.invalidateQueries('workouts-count');
        // Инвалидируем кэш для всех клиентов, чтобы обновить статус выполнения
        queryClient.invalidateQueries('clients');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при обновлении тренировки';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для завершения тренировки
  const completeWorkoutMutation = useMutation(
    () => apiService.updateWorkout(id, { is_completed: true }),
    {
      onSuccess: () => {
        toast.success('Тренировка завершена!');
        queryClient.invalidateQueries(['workout', id]);
        queryClient.invalidateQueries('workouts');
        queryClient.invalidateQueries('workouts-count');
        // Инвалидируем кэш для всех клиентов, чтобы обновить статус выполнения
        queryClient.invalidateQueries('clients');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при завершении тренировки';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для удаления тренировки
  const deleteWorkoutMutation = useMutation(
    () => apiService.deleteWorkout(id),
    {
      onSuccess: () => {
        toast.success('Тренировка удалена успешно!');
        queryClient.invalidateQueries('workouts');
        queryClient.invalidateQueries('workouts-count');
        // Инвалидируем кэш для всех клиентов, чтобы обновить статус выполнения
        queryClient.invalidateQueries('clients');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при удалении тренировки';
        toast.error(errorMessage);
      }
    }
  );

  return {
    // Данные
    workout,
    isLoading,
    error,
    
    // Действия
    updateWorkout: updateWorkoutMutation.mutate,
    completeWorkout: completeWorkoutMutation.mutate,
    deleteWorkout: deleteWorkoutMutation.mutate,
    refetch,
    
    // Состояния мутаций
    isUpdateLoading: updateWorkoutMutation.isLoading,
    isCompleteLoading: completeWorkoutMutation.isLoading,
    isDeleteLoading: deleteWorkoutMutation.isLoading,
  };
};

// Хук для работы с пагинированными тренировками
export const useWorkoutsPaginated = (filters: WorkoutFilters = {}) => {
  const queryClient = useQueryClient();

  // Запрос списка тренировок с пагинацией
  const {
    data,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['workouts-paginated', filters],
    () => apiService.getWorkoutsPaginated(filters),
    {
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при загрузке тренировок';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для создания тренировки
  const createWorkoutMutation = useMutation(
    (data: WorkoutCreate) => apiService.createWorkout(data),
    {
      onSuccess: () => {
        toast.success('Тренировка создана успешно!');
        queryClient.invalidateQueries('workouts-paginated');
        queryClient.invalidateQueries('workouts');
        queryClient.invalidateQueries('clients');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при создании тренировки';
        toast.error(errorMessage);
      }
    }
  );

  // Функция для создания тренировки с кастомным обработчиком успеха
  const createWorkoutWithCallback = (data: WorkoutCreate, onSuccess?: (workout: Workout) => void) => {
    createWorkoutMutation.mutate(data, {
      onSuccess: (workout) => {
        queryClient.invalidateQueries('workouts-paginated');
        queryClient.invalidateQueries('workouts');
        queryClient.invalidateQueries('clients');
        if (onSuccess) {
          onSuccess(workout);
        }
      }
    });
  };

  // Мутация для удаления тренировки
  const deleteWorkoutMutation = useMutation(
    (id: number) => apiService.deleteWorkout(id),
    {
      onSuccess: () => {
        toast.success('Тренировка удалена успешно!');
        queryClient.invalidateQueries('workouts-paginated');
        queryClient.invalidateQueries('workouts');
        queryClient.invalidateQueries('clients');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при удалении тренировки';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для завершения тренировки
  const completeWorkoutMutation = useMutation(
    (id: number) => apiService.updateWorkout(id, { is_completed: true }),
    {
      onSuccess: () => {
        toast.success('Тренировка завершена!');
        queryClient.invalidateQueries('workouts-paginated');
        queryClient.invalidateQueries('workouts');
        queryClient.invalidateQueries('clients');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при завершении тренировки';
        toast.error(errorMessage);
      }
    }
  );

  return {
    // Данные
    workouts: data?.data || [],
    meta: data?.meta,
    isLoading,
    error,
    
    // Действия
    createWorkout: createWorkoutMutation.mutate,
    createWorkoutWithCallback,
    deleteWorkout: deleteWorkoutMutation.mutate,
    completeWorkout: completeWorkoutMutation.mutate,
    refetch,
    
    // Состояния мутаций
    isCreateLoading: createWorkoutMutation.isLoading,
    isDeleteLoading: deleteWorkoutMutation.isLoading,
    isCompleteLoading: completeWorkoutMutation.isLoading,
  };
}; 